import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useQueryClient } from '@tanstack/react-query'
import { PexIcon } from '../../components/icons/PexIcon'
import { Button } from '../../components/ui/button'
import ReportDialog from './ReportDialog'
import { useT } from '../../i18n'
import { useProblemReportState } from '../../lib/problem-reporting/host'
import { reportHostState, reportQueryKey } from '../../lib/problem-reporting/context'
import { reportRequestContext, settleReportRequest } from '../../lib/problem-reporting/context'
import { startReportRecording, type ReportRecording } from '../../lib/problem-reporting/recording'
import { reportCopy } from './copy'
import type { createReportSubmission } from './submission'
import type { createOwnConfirmation } from './confirmation'
import ReportForm from './ReportForm'
import { OwnReportDetail, OwnReports } from './OwnReports'
import ReportNotifications from './ReportNotifications'
export interface ReportAdminSurface {
  selectedReportId: string | null
  onSelectReport: (id: string) => void
  onBack: () => void
}
export type ReporterProps = {
  routeId: string
  renderAdmin?: (surface: ReportAdminSurface) => ReactNode
  showLauncher?: boolean
}
export type ReportControllers = {
  submission: ReturnType<typeof createReportSubmission>
  confirmation: ReturnType<typeof createOwnConfirmation>
}
type RecordingFailure = 'wrongSource' | 'permission' | 'dimensions' | 'format' | 'failed'

function recordingFailure(error: unknown): RecordingFailure {
  if (!(error instanceof Error)) return 'failed'
  if (error.message === 'Choose a browser tab to record') return 'wrongSource'
  if (error.name === 'NotAllowedError' || error.name === 'AbortError') return 'permission'
  if (error.message === 'Recording dimensions unavailable') return 'dimensions'
  if (error.message === 'Recording format unavailable') return 'format'
  return 'failed'
}

export default function ReporterSurface({
  controllers,
  routeId,
  renderAdmin,
  showLauncher = true,
}: ReporterProps & { controllers: ReportControllers }) {
  const t = useT(reportCopy)
  const host = useProblemReportState()
  const client = useQueryClient()
  const state = useSyncExternalStore(
    controllers.submission.subscribe,
    controllers.submission.getState,
    controllers.submission.getState,
  )
  const command = useSyncExternalStore(
    controllers.confirmation.subscribe,
    controllers.confirmation.getState,
    controllers.confirmation.getState,
  )
  const [open, setOpen] = useState(false)
  const [closing, setClosing] = useState(false)
  const [selectedTab, setTab] = useState<'new' | 'own' | 'admin'>('new')
  const [selected, setSelected] = useState<string | null>(null)
  const [selectedAdmin, setSelectedAdmin] = useState<string | null>(null)
  const [approved, setApproved] = useState<ReadonlySet<string>>(new Set())
  const returnFocus = useRef<HTMLElement | null>(null)
  const [businessDialog, setBusinessDialog] = useState<HTMLElement | null>(null)
  const recorder = useRef<ReportRecording | null>(null)
  const recorderRequest = useRef<AbortController | null>(null)
  const recordingAlive = useRef(true)
  const [recordingPhase, setRecordingPhase] = useState<'idle' | 'asking' | 'recording' | 'processing'>('idle')
  const [recordingSeconds, setRecordingSeconds] = useState(0)
  const [recordingError, setRecordingError] = useState<RecordingFailure | null>(null)
  useEffect(() => {
    recordingAlive.current = true
    return () => {
      recordingAlive.current = false
      recorderRequest.current?.abort()
      recorder.current?.cancel()
      recorder.current = null
    }
  }, [host.generation])
  const available = host.phase === 'ready' && Boolean(host.policy?.enabled && host.policy.can_self)
  useEffect(() => {
    if (available || !recorderRequest.current) return
    recorderRequest.current.abort()
    recorderRequest.current = null
    recorder.current?.cancel()
    recorder.current = null
    setRecordingPhase('idle')
    setRecordingSeconds(0)
    setRecordingError(null)
    setOpen(true)
  }, [available])
  const canAdmin =
    host.phase === 'ready' &&
    host.identity?.shell === 'admin' &&
    Boolean(host.policy?.can_review && renderAdmin)
  const tab = selectedTab === 'admin' && !canAdmin ? 'own' : selectedTab
  const recordingErrorMessage = recordingError === 'wrongSource' ? t.recordingWrongSource
    : recordingError === 'permission' ? t.recordingPermission
      : recordingError === 'dimensions' ? t.recordingDimensions
        : recordingError === 'format' ? t.recordingFormat
          : recordingError === 'failed' ? t.recordingFailed : null
  const visible = Boolean(host.identity)
  const locked =
    recordingPhase !== 'idle' ||
    ['pending', 'unknown'].includes(state.outcome) ||
    state.images.some(
      (image) =>
        image.confirmation === 'unresolved' ||
        ['reserving', 'uploading', 'confirming'].includes(image.status),
    ) ||
    ['pending', 'unknown'].includes(command.outcome)
  const openReporter = useCallback(() => {
    if (open) return
    returnFocus.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    void client.invalidateQueries({ queryKey: reportQueryKey('capabilities') })
    setOpen(true)
    setClosing(false)
  }, [client, open])
  useEffect(() => {
    if (!visible) return
    const key = (event: KeyboardEvent) => {
      if (
        event.code === 'KeyB' &&
        event.altKey &&
        event.shiftKey &&
        !event.ctrlKey &&
        !event.metaKey
      ) {
        event.preventDefault()
        openReporter()
      }
    }
    window.addEventListener('keydown', key)
    return () => window.removeEventListener('keydown', key)
  }, [visible, openReporter])
  useEffect(() => {
    if (!visible) return
    const handleOpen = (event: Event) => {
      const detail = (event as CustomEvent<{ tab?: unknown; returnFocus?: unknown }>).detail
      if (detail?.tab !== 'new' && detail?.tab !== 'own') return
      setTab(detail.tab)
      openReporter()
      if (detail.returnFocus instanceof HTMLElement) returnFocus.current = detail.returnFocus
    }
    window.addEventListener('pexify:open-problem-report', handleOpen)
    return () => window.removeEventListener('pexify:open-problem-report', handleOpen)
  }, [visible, openReporter])
  useEffect(() => {
    if (!visible || open) return
    const update = () =>
      setBusinessDialog(
        Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"][data-state="open"]'))
          .filter((element) => !element.hasAttribute('data-problem-report-overlay'))
          .at(-1) ?? null,
      )
    update()
    const observer = new MutationObserver(update)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state'],
    })
    return () => observer.disconnect()
  }, [visible, open])
  if (!visible) return null
  const finishRecorded = async (request: AbortController, blob: Blob) => {
    if (request.signal.aborted || !recordingAlive.current || recorderRequest.current !== request) return
    recorder.current = null
    setRecordingPhase('processing')
    const key = await controllers.submission.addVideo(blob)
    if (request.signal.aborted || !recordingAlive.current || recorderRequest.current !== request) return
    recorderRequest.current = null
    setRecordingPhase('idle')
    setRecordingError(key ? null : 'failed')
    setOpen(true)
  }
  const startRecording = async () => {
    if (recorderRequest.current || recordingPhase !== 'idle' || !available || state.images.length >= 3) return
    const request = new AbortController()
    recorderRequest.current = request
    setRecordingPhase('asking')
    setRecordingError(null)
    let session: ReportRecording | null = null
    try {
      const context = reportRequestContext()
      session = await startReportRecording({
        signal: request.signal,
        onProgress: seconds => { if (!request.signal.aborted && recordingAlive.current) setRecordingSeconds(seconds) },
        onFinished: blob => { void finishRecorded(request, blob) },
        onError: error => {
          if (!request.signal.aborted && recordingAlive.current) {
            request.abort()
            recorder.current = null
            recorderRequest.current = null
            setRecordingPhase('idle'); setRecordingError(recordingFailure(error)); setOpen(true)
          }
        },
      })
      await settleReportRequest(context, request.signal)
      if (request.signal.aborted || !recordingAlive.current) { session.cancel(); return }
      recorder.current = session
      setRecordingSeconds(0)
      setRecordingPhase('recording')
      setClosing(false)
      setOpen(false)
    } catch (error) {
      session?.cancel()
      if (!request.signal.aborted && recordingAlive.current) {
        recorderRequest.current = null
        setRecordingPhase('idle'); setRecordingError(recordingFailure(error)); setOpen(true)
      }
    }
  }
  const stopRecording = async () => {
    const session = recorder.current, request = recorderRequest.current
    if (!session || !request || recordingPhase !== 'recording') return
    setRecordingPhase('processing')
    try { await finishRecorded(request, await session.stop()) }
    catch (error) {
      if (!request.signal.aborted && recordingAlive.current) {
        recorder.current = null; recorderRequest.current = null
        setRecordingPhase('idle'); setRecordingError(recordingFailure(error)); setOpen(true)
      }
    }
  }
  const requestClose = () => {
    if (locked || controllers.submission.requestClose() === 'confirm-discard') setClosing(true)
    else setOpen(false)
  }
  const viewOwn = (id: string) => {
    setSelected(id)
    setTab('own')
  }
  const viewAdmin = (id: string) => {
    if (canAdmin && reportHostState().policy?.can_review) {
      setSelectedAdmin(id)
      setTab('admin')
    }
  }
  const launcher = showLauncher && !open && (
    <Button
      data-problem-report-overlay
      aria-keyshortcuts="Alt+Shift+B"
      variant="outline"
      size="sm"
      onClick={openReporter}
      className="fixed bottom-4 left-4 z-[80] shadow-[var(--shadow-float)]"
    >
      <PexIcon name="help" size="sm" className="mr-2" />
      {t.title}
      <span className="ml-2 hidden text-xs text-[var(--color-text-muted)] sm:inline">
        Alt+Shift+B
      </span>
    </Button>
  )
  const stopControl = !open && recordingPhase === 'recording' && (
    <Button
      data-problem-report-overlay variant="outline" size="sm"
      className="fixed bottom-4 left-4 z-[90] border-[var(--color-border-strong)] bg-[var(--color-surface)] shadow-[var(--shadow-float)]"
      onClick={() => void stopRecording()}
    ><span aria-hidden="true" className="mr-2 inline-block size-2 rounded-full bg-red-600" />
      {t.stopRecording} · {recordingSeconds}s / 59s</Button>
  )
  return (
    <>
      {businessDialog?.isConnected ? createPortal(launcher, businessDialog) : launcher}
      {businessDialog?.isConnected ? createPortal(stopControl, businessDialog) : stopControl}
      <ReportDialog
        open={open}
        onRequestClose={requestClose}
        tab={tab}
        setTab={setTab}
        canAdmin={canAdmin}
        onCloseAutoFocus={(event) => {
          const target = returnFocus.current
          if (target?.isConnected) {
            event.preventDefault()
            target.focus()
          }
        }}
        footer={
          <>
            {!closing &&
              tab === 'new' &&
              (state.outcome === 'saved' ? (
                <Button
                  className="w-full"
                  disabled={locked}
                  onClick={() => {
                    controllers.submission.discard()
                    setApproved(new Set())
                    void client.invalidateQueries({
                      queryKey: reportQueryKey('capabilities'),
                    })
                  }}
                >
                  {t.nextReport}
                </Button>
              ) : (
                <Button
                  className="w-full"
                  disabled={
                    state.outcome === 'pending' ||
                    state.preparing ||
                    recordingPhase !== 'idle' ||
                    (state.outcome !== 'unknown' &&
                      (!available ||
                        state.images.some(
                          (image) => !image.previewUrl || !approved.has(image.previewUrl),
                        )))
                  }
                  onClick={() => void controllers.submission.send(routeId)}
                >
                  {state.outcome === 'unknown'
                    ? t.recover
                    : state.outcome === 'pending'
                      ? t.sending
                      : t.send}
                </Button>
              ))}
            {!closing && tab !== 'new' && (
              <Button variant="ghost" className="w-full" onClick={requestClose}>
                {t.close}
              </Button>
            )}
          </>
        }
      >
        <div
          id="report-tab-panel"
          role="tabpanel"
          aria-labelledby={`report-tab-${tab}`}
          className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6"
        >
          {closing ? (
            <div className="space-y-4 text-sm">
              <p>{locked ? t.closePending : t.closeDraft}</p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setClosing(false)}>{t.return}</Button>
                {!locked && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      controllers.submission.discard()
                      setApproved(new Set())
                      setClosing(false)
                      setOpen(false)
                    }}
                  >
                    {t.discard}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  onClick={() => {
                    setClosing(false)
                    setOpen(false)
                  }}
                >
                  {t.keepClose}
                </Button>
              </div>
            </div>
          ) : (
            <>
              {tab === 'new' && (
                <><ReportForm
                  controller={controllers.submission}
                  state={state}
                  available={available}
                  approved={approved}
                  onApproval={(url, checked) =>
                    setApproved((previous) => {
                      const next = new Set(previous)
                      if (checked) next.add(url)
                      else next.delete(url)
                      return next
                    })
                  }
                  onView={viewOwn}
                  onRecord={() => void startRecording()}
                  recordingBusy={recordingPhase !== 'idle'}
                  recordingErrorMessage={recordingErrorMessage}
                /></>
              )}
              {tab === 'own' &&
                command.id &&
                ['pending', 'unknown'].includes(command.outcome) &&
                selected !== command.id && (
                  <Button variant="outline" onClick={() => viewOwn(command.id!)}>
                    {t.recover}
                  </Button>
                )}
              {tab === 'own' &&
                (selected ? (
                  <OwnReportDetail
                    id={selected}
                    confirmation={controllers.confirmation}
                    onBack={() => setSelected(null)}
                  />
                ) : (
                  <div className="space-y-6">
                    <OwnReports onSelect={viewOwn} />
                    <ReportNotifications
                      onOwn={viewOwn}
                      onAdmin={canAdmin ? viewAdmin : undefined}
                    />
                  </div>
                ))}
              {tab === 'admin' &&
                canAdmin &&
                renderAdmin?.({
                  selectedReportId: selectedAdmin,
                  onSelectReport: setSelectedAdmin,
                  onBack: () => setSelectedAdmin(null),
                })}
            </>
          )}
        </div>
      </ReportDialog>
    </>
  )
}
